import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch, ApiError } from '../lib/api';
import { SURVEY_QUESTIONS, DIMENSION_LABELS, type SurveyDimension } from '../config/surveyQuestions';

// Sprint ACAD ACT.A7 (#1708) — End-of-Programme Survey, student-facing page.
//
// AC requirement: "Students complete survey via a dedicated survey page
// (not within a phase agent session)." This is a standalone route
// (/cohorts/:cohortId/survey), not nested under any project/ChatPage
// layout — there is no agent session involved at all (Q1 — no
// conversational "Retrospective Agent" exists to host this inside).
//
// Prefills from GET /results (the STUDENT-shaped response — the caller's
// own row only, per Q5/Q8) so a student can see/edit prior answers before
// the launch is resubmitted (Q6 — submission is an upsert).

interface StudentAnswer {
  dimension:     SurveyDimension;
  questionIndex: number;
  score:         number;
}

interface StudentResultsResponse {
  launchId:   string | null;
  launchedAt: string | null;
  aggregate:  { byDimension: Record<string, number>; overallMean: number | null };
  individual: StudentAnswer[];
}

function answerKey(dimension: string, questionIndex: number): string {
  return `${dimension}:${questionIndex}`;
}

export default function SurveyPage() {
  const { cohortId } = useParams<{ cohortId: string }>();
  const [launchId, setLaunchId] = useState<string | null>(null);
  const [scores, setScores]     = useState<Record<string, number>>({});
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [noLaunch, setNoLaunch] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  // R1+R2 post-triage fix: a relaunch that races an in-progress submission
  // is now an explicit 409 (STALE_LAUNCH) from the backend rather than a
  // silent misattribution — this flag drives a distinct "please refresh"
  // message instead of the generic error banner.
  const [stale, setStale] = useState(false);

  const loadSurvey = () => {
    if (!cohortId) return;
    setLoading(true);
    setStale(false);
    apiFetch<StudentResultsResponse>(`/api/academic/cohorts/${cohortId}/survey/results`)
      .then((res) => {
        if (!res.launchId) {
          setNoLaunch(true);
        } else {
          setNoLaunch(false);
          setLaunchId(res.launchId);
          const prefill: Record<string, number> = {};
          for (const answer of res.individual) {
            prefill[answerKey(answer.dimension, answer.questionIndex)] = answer.score;
          }
          setScores(prefill);
        }
        setLoading(false);
      })
      .catch((e) => {
        setError(e instanceof ApiError ? e.message : 'Failed to load survey');
        setLoading(false);
      });
  };

  useEffect(loadSurvey, [cohortId]);

  const questionsByDimension = useMemo(() => {
    const grouped = new Map<SurveyDimension, typeof SURVEY_QUESTIONS>();
    for (const q of SURVEY_QUESTIONS) {
      const existing = grouped.get(q.dimension) ?? [];
      existing.push(q);
      grouped.set(q.dimension, existing);
    }
    return grouped;
  }, []);

  const allAnswered = SURVEY_QUESTIONS.every(q => scores[answerKey(q.dimension, q.questionIndex)] !== undefined);

  async function handleSubmit() {
    if (!cohortId || !allAnswered || !launchId) return;
    setSubmitting(true);
    setError(null);
    try {
      const answers = SURVEY_QUESTIONS.map(q => ({
        dimension:     q.dimension,
        questionIndex: q.questionIndex,
        score:         scores[answerKey(q.dimension, q.questionIndex)],
      }));
      await apiFetch(`/api/academic/cohorts/${cohortId}/survey/responses`, {
        method: 'POST',
        body: JSON.stringify({ launchId, answers }),
      });
      setSubmitted(true);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.body?.code === 'STALE_LAUNCH') {
        setStale(true);
      } else {
        setError(e instanceof ApiError ? e.message : 'Failed to submit survey');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const cardStyle: React.CSSProperties = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--space-6)',
  };

  return (
    <main style={{ flex: 1, padding: 'var(--space-8)', maxWidth: 800, margin: '0 auto', width: '100%' }}>
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
          End-of-Programme Survey
        </h1>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
          Your feedback shapes future cohorts. Rate each statement from 1 (strongly disagree) to 5 (strongly agree).
        </p>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}>Loading…</p>
      ) : noLaunch ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 'var(--space-12)' }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--text-secondary)' }}>
            No survey has been launched for your cohort yet.
          </p>
        </div>
      ) : submitted ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 'var(--space-12)' }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--text)' }}>
            Thank you — your responses have been recorded.
          </p>
        </div>
      ) : stale ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 'var(--space-12)' }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--text)', marginBottom: 'var(--space-4)' }}>
            This survey has been relaunched since you opened it. Please refresh to answer the current version.
          </p>
          <button
            type="button"
            onClick={loadSurvey}
            style={{
              padding: '10px 24px', borderRadius: 'var(--radius-md)', border: 'none',
              background: 'var(--primary)', color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Refresh
          </button>
        </div>
      ) : (
        <>
          {[...questionsByDimension.entries()].map(([dimension, questions]) => (
            <div key={dimension} style={{ ...cardStyle, marginBottom: 'var(--space-4)' }}>
              <h2 style={{ fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 600, color: 'var(--text)', margin: '0 0 var(--space-4)' }}>
                {DIMENSION_LABELS[dimension]}
              </h2>
              {questions.map(q => {
                const key = answerKey(q.dimension, q.questionIndex);
                return (
                  <div key={key} style={{ marginBottom: 'var(--space-4)' }}>
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text)', margin: '0 0 8px' }}>
                      {q.text}
                    </p>
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                      {[1, 2, 3, 4, 5].map(value => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setScores(prev => ({ ...prev, [key]: value }))}
                          style={{
                            width: 40, height: 40, borderRadius: 'var(--radius-md)',
                            border: scores[key] === value ? '2px solid var(--primary)' : '1px solid var(--border)',
                            background: scores[key] === value ? 'var(--primary)' : 'var(--surface)',
                            color: scores[key] === value ? 'white' : 'var(--text)',
                            fontSize: 14, fontWeight: 600, cursor: 'pointer',
                          }}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {error && (
            <p style={{ color: 'var(--error)', fontFamily: 'var(--font-body)', marginBottom: 'var(--space-4)' }}>{error}</p>
          )}

          <button
            type="button"
            disabled={!allAnswered || submitting}
            onClick={handleSubmit}
            style={{
              padding: '10px 24px', borderRadius: 'var(--radius-md)', border: 'none',
              background: allAnswered ? 'var(--primary)' : 'var(--border)',
              color: 'white', fontSize: 14, fontWeight: 600,
              cursor: allAnswered && !submitting ? 'pointer' : 'not-allowed',
            }}
          >
            {submitting ? 'Submitting…' : 'Submit Survey'}
          </button>
        </>
      )}
    </main>
  );
}
